import React from 'react';
import PropTypes from 'prop-types';
import { gql } from '@apollo/client';
import { graphql } from '@apollo/client/react/hoc';
import { compose } from 'recompose';
import isEmpty from 'lodash/isEmpty';
import findIndex from 'lodash/findIndex';
import get from 'lodash/get';
import { LoaderKeetUI } from '@keethealth/ui-components';
import Questionnaire from 'scenes/Questionnaire';
import { Content } from 'scenes/Education';
import TaskDetail from 'scenes/TaskDetail';
import { findNext } from 'utils/utils';
import { orderByDate, transformActivities } from 'utils/carePlans';
import observationProps from 'test_constants/observation';
import '../care_plan.scss';

/** Lightweight: activity header only. Full sibling list comes from CarePlan (carePlansIncomplete). */
const CARE_PLAN_ACTIVITY_FOR_TASK = gql`
  query carePlanActivityForTask($id: ID!) {
    carePlanActivity(id: $id) {
      id
      carePlanId
      properties {
        count
        frequency
        duration
        durationUnit
        period
        periodUnit
      }
      plannable {
        __typename
        ... on Education {
          id
        }
        ... on Exercise {
          id
        }
        ... on Questionnaire {
          id
        }
      }
    }
  }
`;

function CarePlanActivity({
  loading,
  match,
  carePlanActivity,
  carePlans,
  education,
  onBack,
}) {
  const { path, params : { taskId } } = match;
  if (loading && !carePlanActivity) return <LoaderKeetUI color="primary" />;
  if (!carePlanActivity?.plannable) return null;
  if (path.includes('/observation')) {
    return <Questionnaire {...observationProps} getNextActivity={getNextActivity} />;
  }
  switch (carePlanActivity.plannable.__typename) {
    case 'Questionnaire':
      return (
        <Questionnaire
          loading={loading}
          match={match}
          carePlanActivity={carePlanActivity}
          getNextActivity={getNextActivity}
          onBack={onBack}
        />
      );
    case 'Education':
      return (
        <Content
          loading={loading}
          education={education}
          match={match}
          carePlanActivity={carePlanActivity}
          getNextActivity={getNextActivity}
          onBack={onBack}
        />
      );
    case 'Exercise':
      return (
        <TaskDetail
          loading={loading}
          match={match}
          carePlanActivity={carePlanActivity}
          getNextActivity={getNextActivity}
          onBack={onBack}
        />
      );
    default:
      return null;
  }
  function getNextActivity() {
    const currentTypename = get(carePlanActivity, 'plannable.__typename');
    if (!currentTypename) return null;

    const activeActivities = transformActivities(carePlans || []);
    const sameTypeActivities = orderByDate(
      activeActivities.filter(
        (a) => get(a, 'plannable.__typename') === currentTypename,
      ),
      'scheduledAt',
      'desc',
    );

    if (isEmpty(sameTypeActivities)) return null;

    const currentIndex = findIndex(sameTypeActivities, { id: taskId });
    const nextActivity = findNext(
      sameTypeActivities,
      currentIndex,
      (cpa) => cpa.status !== 'completed',
    );
    if (nextActivity) return nextActivity.id;
    return null;
  }
}

CarePlanActivity.propTypes = {
  onBack: PropTypes.func,
  loading: PropTypes.bool.isRequired,
  match: PropTypes.shape({
    path: PropTypes.string,
    params: PropTypes.shape({
      taskId: PropTypes.string,
    }),
  }).isRequired,
  carePlanActivity: PropTypes.shape({
    plannable: PropTypes.shape({
      __typename: PropTypes.string.isRequired,
    }),
  }),
  carePlans: PropTypes.arrayOf(
    PropTypes.shape({
      carePlanActivities: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
          required: PropTypes.bool,
        }),
      ),
    }),
  ),
  education: PropTypes.object,
};

CarePlanActivity.defaultProps = {
  carePlans: [],
  carePlanActivity: undefined,
  onBack: undefined,
};

export default compose(
  graphql(CARE_PLAN_ACTIVITY_FOR_TASK, {
    props: ({ data: { loading, carePlanActivity } }) => ({
      loading,
      carePlanActivity,
    }),
    options: ({ match: { params: { taskId: id } } }) => ({
      variables: { id },
      fetchPolicy: 'cache-and-network',
    }),
  }),
)(CarePlanActivity);
